import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { onLobbyUpdate, submitCards, selectWinner, revealSubmission, exchangeHandForPoint, startNextRound, upvoteSubmission, createLobby } from '../lib/firebase/collections';
import type { Lobby } from '../types/game';
import { useAuth } from '../contexts/AuthContext';
import Confetti from 'react-confetti';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import GameOver from './GameOver';

export default function GameBoard() {
  const { lobbyId } = useParams<{ lobbyId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [lobby, setLobby] = useState<Lobby | null>(null);
  const [selectedCards, setSelectedCards] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [votedSubmissions, setVotedSubmissions] = useState<string[]>([]);

  const gamePhase = lobby?.gamePhase || 'selecting';
  const revealedSubmissionIndices = lobby?.revealedSubmissionIndices || [];

  useEffect(() => {
    if (!lobbyId) {
      navigate('/');
      return;
    }
    const unsubscribe = onLobbyUpdate(lobbyId, (lobbyData) => {
      if (lobbyData) {
        if (lobbyData.roundNumber !== lobby?.roundNumber) {
          setSelectedCards([]);
          setVotedSubmissions([]);
        }
        setLobby(lobbyData);
      } else {
        setError('Spiel nicht gefunden.');
        navigate('/');
      }
      setIsLoading(false);
    }, (err) => {
      console.error(err);
      setError('Fehler beim Laden des Spiels.');
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, [lobbyId, navigate, lobby?.roundNumber]);

  useEffect(() => {
    if (lobby?.gamePhase !== 'selecting' || !lobby.roundEndTime) {
      setTimeLeft(null);
      return;
    }

    // `roundEndTime` ist ein Firestore-Timestamp, muss in ein Date-Objekt umgewandelt werden
    const endTime = (lobby.roundEndTime as any).toDate();
    
    const calculateTimeLeft = () => {
      const difference = endTime.getTime() - new Date().getTime();
      return difference > 0 ? Math.floor(difference / 1000) : 0;
    };

    setTimeLeft(calculateTimeLeft());

    const interval = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);
      
      // Wenn Timer abgelaufen und wir immer noch in der Selecting-Phase sind
      if (newTimeLeft === 0 && lobby.gamePhase === 'selecting') {
        // Automatisch zur Judging-Phase wechseln
        handleTimerExpired();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [lobby?.roundEndTime, lobby?.gamePhase]);

  // Neue Funktion zum Behandeln des abgelaufenen Timers
  const handleTimerExpired = async () => {
    if (!lobbyId || !lobby) return;
    
    try {
      // Aktualisiere die Lobby zur Judging-Phase
      const lobbyRef = doc(db, 'lobbies', lobbyId);
      await updateDoc(lobbyRef, {
        gamePhase: 'judging',
        // Optional: Markiere Spieler, die nicht eingereicht haben
        submissions: lobby.submissions || []
      });
    } catch (error) {
      console.error('Fehler beim Wechseln zur Judging-Phase:', error);
    }
  };

  const currentPlayer = lobby?.players.find(p => p.uid === user?.uid);
  const isHost = lobby?.hostUid === user?.uid;
  const isCardCzar = currentPlayer?.isCzar || false;
  const hasSubmitted = (lobby?.submissions || []).some(s => s.playerUid === user?.uid) || false;
  
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCardSelect = (cardId: string) => {
    if (isCardCzar || hasSubmitted || gamePhase !== 'selecting') return;
    
    const maxCards = lobby?.currentBlackCard?.pick || 1;

    setSelectedCards(prev => {
      // Karte abwählen
      if (prev.includes(cardId)) {
        return prev.filter(id => id !== cardId);
      }
      
      // Wenn die Auswahl voll ist, die erste Karte entfernen und die neue hinzufügen
      if (prev.length >= maxCards) {
        return [...prev.slice(1), cardId];
      }

      // Karte zur Auswahl hinzufügen
      return [...prev, cardId];
    });
  };

  const handleSubmitCards = async () => {
    if (!lobbyId || !user || selectedCards.length === 0) return;
    try {
      await submitCards(lobbyId, user.uid, selectedCards);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Einreichen');
    }
  };
  
  const handleRevealClick = async (index: number) => {
    if (!lobbyId || !isCardCzar || gamePhase !== 'judging' || revealedSubmissionIndices.includes(index)) return;
    await revealSubmission(lobbyId, index);
  };
  
  const handleSelectWinner = async (winnerUid: string) => {
    if (!lobbyId || !isCardCzar) return;
    await selectWinner(lobbyId, winnerUid);
  };

  const handleExchangeHand = async () => {
    if (!lobbyId || !user) return;
    if (window.confirm("Möchtest du wirklich 1 Punkt für eine neue Hand ausgeben?")) {
      try {
        await exchangeHandForPoint(lobbyId, user.uid);
      } catch (err: any) {
        setError(err.message || "Fehler beim Tauschen der Hand.");
      }
    }
  };

  const handleUpvote = async (submissionPlayerUid: string) => {
    if (!lobbyId || votedSubmissions.includes(submissionPlayerUid)) return;
    try {
      await upvoteSubmission(lobbyId, submissionPlayerUid);
      setVotedSubmissions(prev => [...prev, submissionPlayerUid]);
    } catch (error) {
      console.error("Upvote fehlgeschlagen:", error);
    }
  };

  if (isLoading) return <div className="flex items-center justify-center h-screen bg-gray-900"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400"></div></div>;
  if (error || !lobby || !currentPlayer) return <div className="container mx-auto p-4 text-center"><div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded">{error || 'Spiel konnte nicht geladen werden.'}</div></div>;

  // Zeige GameOver-Komponente, wenn das Spiel beendet ist
  if (lobby.gameState === 'ended') {
    return (
      <div className="min-h-screen w-full bg-gradient-to-br from-gray-900 via-[#10181f] to-[#1a202c] p-3 sm:p-4 font-sans">
        <GameOver 
          players={lobby.players}
          onRestart={async () => {
            // Neue Lobby mit gleichen Einstellungen erstellen
            try {
              const lobbyId = await createLobby(
                lobby.hostName,
                lobby.hostUid,
                lobby.selectedCategoryId,
                lobby.gameSettings || {
                  timerEnabled: true,
                  roundTimeSeconds: 90,
                  winningScore: 10,
                  allowLateJoin: true,
                  showPlayerNames: false,
                  autoStartNextRound: true,
                  handSize: 7,
                  allowSkipCards: true
                }
              );
              navigate(`/lobby/${lobbyId}`);
            } catch (error) {
              console.error('Fehler beim Erstellen einer neuen Lobby:', error);
            }
          }}
          onReturnToLobby={() => navigate('/')}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-gray-900 via-[#10181f] to-[#1a202c] p-3 sm:p-4 font-sans">
      {gamePhase === 'results' && lobby.lastWinner && <Confetti recycle={false} numberOfPieces={600} gravity={0.3} />}
      <div className="max-w-7xl mx-auto">
        <header className="bg-black/20 backdrop-blur-xl border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <h1 className="text-2xl sm:text-3xl font-bold text-white font-display">Runde {lobby.roundNumber}</h1>
            <div className="flex items-center gap-4">
              {timeLeft !== null && (
                <div className={`text-xl sm:text-2xl font-bold ${timeLeft <= 10 ? 'text-red-400' : 'text-green-300'}`}>
                  {formatTime(timeLeft)}
                </div>
              )}
              <div className="text-xs sm:text-sm text-gray-400 uppercase tracking-wider font-semibold text-center">
                {gamePhase === 'selecting' && 'Wähle deine Antwort'}
                {gamePhase === 'judging' && 'Czar wählt den Gewinner'}
                {gamePhase === 'results' && `Gewinner: ${lobby.lastWinner?.playerName}`}
              </div>
            </div>
          </div>
        </header>

        <main>
          {gamePhase === 'results' && lobby.lastWinner ? (
            <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center py-6 sm:py-10">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-amber-500 mb-4 font-display break-words">
                {lobby.lastWinner.isGameWinner ? "Gesamtsieger!" : "Gewinner der Runde!"}
              </h2>
              <h3 className="text-xl sm:text-2xl lg:text-3xl text-white mb-6 sm:mb-8 break-words">{lobby.lastWinner.playerName}</h3>
              <div className="bg-black border-2 border-gray-600 rounded-xl p-4 sm:p-6 text-center shadow-2xl max-w-2xl mx-auto mb-4 sm:mb-6">
                <p className="text-lg sm:text-xl lg:text-2xl text-white leading-relaxed font-display break-words">{lobby.currentBlackCard?.text.replace(/_/g, '___')}</p>
              </div>
              <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
                {lobby.lastWinner.cards.map(card => (
                  <motion.div key={card.id} className="bg-white rounded-xl p-4 sm:p-6 text-gray-900 font-bold w-full max-w-72 text-center text-base sm:text-lg shadow-lg">
                    {card.text}
                  </motion.div>
                ))}
              </div>
              {isHost && (
                <motion.button whileHover={{scale: 1.05}} whileTap={{scale: 0.95}} onClick={() => startNextRound(lobbyId!)} className="mt-6 sm:mt-10 px-6 sm:px-10 py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-bold transition-all text-base sm:text-lg shadow-lg">
                  Nächste Runde starten
                </motion.button>
              )}
            </motion.div>
          ) : (
            <>
              {lobby.currentBlackCard && (
                <div className="mb-6 sm:mb-8">
                  <motion.div 
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-black border-2 border-gray-600 rounded-xl sm:rounded-2xl p-6 sm:p-8 text-center shadow-2xl"
                  >
                    <h2 className="text-lg sm:text-xl font-bold text-white mb-4 font-display uppercase tracking-wider">Schwarze Karte</h2>
                    <p className="text-xl sm:text-2xl lg:text-3xl text-white leading-relaxed font-display break-words" dangerouslySetInnerHTML={{ __html: lobby.currentBlackCard.text.replace(/_/g, '<span class="inline-block w-20 sm:w-24 h-px bg-white"></span>') }}></p>
                    <div className="mt-4 text-xs sm:text-sm text-gray-400">Wähle {lobby.currentBlackCard.pick} Karte(n)</div>
                  </motion.div>
                </div>
              )}
              
              {/* Czar View */}
              {isCardCzar && gamePhase === 'judging' && (
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 text-center font-display">Wähle den Gewinner:</h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    <AnimatePresence>
                      {(lobby.submissions || []).map((submission, index) => {
                        const isRevealed = revealedSubmissionIndices.includes(index);
                        return (
                          <motion.div key={submission.playerUid} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col">
                            <div onClick={() => handleRevealClick(index)} className={`bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-3 sm:p-4 transition-all flex-grow ${!isRevealed && 'cursor-pointer hover:border-white/30'}`}>
                              {submission.cards.map((card) => (
                                <div key={card.id} className="bg-white rounded-lg p-3 sm:p-4 text-gray-900 font-semibold h-20 sm:h-24 flex items-center justify-center text-center mb-2 text-sm sm:text-base">
                                  {isRevealed ? card.text : <span className="text-gray-400 italic">Karte</span>}
                                </div>
                              ))}
                            </div>
                            {isRevealed && (
                              <div className="flex justify-between items-center mt-3">
                                <button onClick={(e) => { e.stopPropagation(); handleUpvote(submission.playerUid); }} disabled={votedSubmissions.includes(submission.playerUid)} className="flex items-center gap-2 text-xs sm:text-sm text-pink-400 disabled:text-gray-500 hover:text-pink-300 transition-colors">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" /></svg>
                                  <span>{submission.votes || 0}</span>
                                </button>
                                <button onClick={() => handleSelectWinner(submission.playerUid)} className="py-2 px-3 sm:px-4 bg-green-600 hover:bg-green-700 rounded-lg font-bold transition-all text-xs">
                                  Zum Gewinner wählen
                                </button>
                              </div>
                            )}
                          </motion.div>
                        );
                      })}
                    </AnimatePresence>
                  </div>
                </div>
              )}

              {/* Player View (Judging) */}
              {!isCardCzar && gamePhase === 'judging' && (
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 text-center font-display">Der Card Czar wählt...</h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                     {(lobby.submissions || []).map((submission, index) => {
                        const isRevealed = revealedSubmissionIndices.includes(index);
                        return (
                          <motion.div key={submission.playerUid} layout className={`bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-3 sm:p-4 transition-all ${isRevealed ? 'ring-2 ring-blue-400' : ''}`}>
                            {submission.cards.map((card) => (
                              <div key={card.id} className="bg-white rounded-lg p-3 sm:p-4 text-gray-900 font-semibold h-20 sm:h-24 flex items-center justify-center text-center mb-2 text-sm sm:text-base">
                                {isRevealed ? card.text : <span className="text-gray-400 italic">Verdeckt</span>}
                              </div>
                            ))}
                            {isRevealed && (
                              <div className="flex justify-start items-center mt-3">
                                <button onClick={(e) => { e.stopPropagation(); handleUpvote(submission.playerUid); }} disabled={votedSubmissions.includes(submission.playerUid)} className="flex items-center gap-2 text-xs sm:text-sm text-pink-400 disabled:text-gray-500 hover:text-pink-300 transition-colors">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" /></svg>
                                  <span>{submission.votes || 0}</span>
                                </button>
                              </div>
                            )}
                          </motion.div>
                        )
                     })}
                  </div>
                </div>
              )}

              {/* Player View (Selecting) */}
              {!isCardCzar && !hasSubmitted && gamePhase === 'selecting' && (
                 <div>
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-4">
                    <h2 className="text-xl sm:text-2xl font-bold text-white font-display">Deine Hand ({selectedCards.length}/{lobby.currentBlackCard?.pick || 1})</h2>
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full sm:w-auto">
                      {(lobby.gameSettings?.allowSkipCards && currentPlayer.score > 0 && !currentPlayer.hasExchangedHand) && (
                         <button onClick={handleExchangeHand} className="px-3 sm:px-4 py-2 sm:py-3 bg-yellow-600 hover:bg-yellow-700 text-white rounded-xl font-bold transition-transform text-sm sm:text-base">Hand tauschen (1 Punkt)</button>
                      )}
                      <button onClick={handleSubmitCards} disabled={selectedCards.length !== (lobby.currentBlackCard?.pick || 1)} className="px-4 sm:px-6 py-2 sm:py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-transform text-sm sm:text-base">Einreichen</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
                    {currentPlayer.hand.map((card) => (
                      <motion.div key={card.id} onClick={() => handleCardSelect(card.id)} className={`bg-white rounded-xl p-3 sm:p-4 cursor-pointer transition-all shadow-lg ${selectedCards.includes(card.id) ? 'ring-4 ring-green-400 scale-105' : 'hover:scale-105 hover:shadow-xl'}`}><p className="text-gray-900 text-sm sm:text-base lg:text-lg leading-relaxed font-semibold">{card.text}</p></motion.div>
                    ))}
                  </div>
                </div>
              )}

              {!isCardCzar && hasSubmitted && gamePhase !== 'judging' && (
                <div className="text-center py-12 sm:py-16"><div className="bg-green-900/50 border border-green-500 text-green-200 px-6 sm:px-8 py-4 sm:py-6 rounded-2xl inline-block shadow-lg"><h2 className="text-xl sm:text-2xl font-bold mb-2 font-display">Karten eingereicht!</h2><p className="text-gray-300 text-sm sm:text-base">Warte auf die anderen Spieler...</p></div></div>
              )}
            </>
          )}
        </main>
        
        {/* Players Footer */}
        <footer className="mt-6 sm:mt-8 bg-black/20 backdrop-blur-xl border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-bold text-white mb-3 sm:mb-4 font-display">Spieler</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-4">
            {lobby.players.map((p) => (
              <div key={p.uid} className={`flex items-center justify-between p-2 sm:p-3 rounded-lg sm:rounded-xl border-2 ${ p.isCzar ? 'border-yellow-400 bg-yellow-500/20' : (lobby.submissions || []).some(s => s.playerUid === p.uid) ? 'border-green-400 bg-green-500/20' : 'border-white/10 bg-white/5' }`}>
                <span className="text-white font-semibold text-xs sm:text-sm truncate pr-1">{p.name}</span>
                <span className="text-white font-bold text-sm sm:text-lg flex-shrink-0">{p.score}</span>
              </div>
            ))}
          </div>
        </footer>
      </div>
    </div>
  );
} 